var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { DataCache } from '@shared/commons/DataCache';
import GoogleAuthenticatorTemplate from '@front/templates/GoogleAuthenticatorTemplate.html';
import { ContainerControl } from '../elements/ContainerControl';
import { Resources } from '@shared/localizations/Resources';
import { ControlsTypes } from '../UtilsClasses/FactoryConstants';
export class GoogleAuthenticator extends ContainerControl {
    constructor() {
        super();
    }
    getType() {
        return ControlsTypes.GoogleAuthenticator;
    }
    oninit() {
        super.oninit();
        this.observe('currentStep', this.handleStepChange.bind(this));
        this.localize();
    }
    localize() {
        void this.set({
            stepProgressLabels: [
                Resources.getResource('screen.additionalVerification.googleAuth.StepOneProgressLabel'),
                Resources.getResource('screen.additionalVerification.googleAuth.StepTwoProgressLabel'),
                Resources.getResource('screen.additionalVerification.googleAuth.StepThreeProgressLabel')
            ],
            firstStepText: Resources.getResource('screen.additionalVerification.googleAuth.FirstStepText'),
            secondStepText: Resources.getResource('screen.additionalVerification.googleAuth.SecondStepText'),
            thirdStepText: Resources.getResource('screen.additionalVerification.googleAuth.ThirdStepText'),
            userCodeLabel: Resources.getResource('screen.additionalVerification.googleAuth.UserCodeLabel'),
            secretKeyLabel: Resources.getResource('screen.additionalVerification.googleAuth.SecretKeyLabel'),
            backText: Resources.getResource('pageNavigation.backButton'),
            nextText: Resources.getResource('pageNavigation.nextButton'),
            errorText: Resources.getResource('screen.additionalVerification.googleAuth.ErrorText'),
            finishText: Resources.getResource('screen.additionalVerification.googleAuth.FinishText')
        });
    }
    backClick() {
        const currentStep = this.get('currentStep');
        if (currentStep > GoogleAuthenticator.StepInitial) {
            void this.set('currentStep', currentStep - 1);
        }
    }
    nextClick() {
        const currentStep = this.get('currentStep');
        if (currentStep < GoogleAuthenticator.StepsCount) {
            void this.set('currentStep', currentStep + 1);
        }
    }
    finishClick() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                void this.set('haveError', false);
                const userID = this.get('userID');
                const userCodeText = this.get('userCodeText');
                const encryptionKey = this.get('secretKeyText');
                if (userID != null && userCodeText != null) {
                    const requestData = {
                        userID,
                        secretKey: encryptionKey,
                        verificationPassword: userCodeText
                    };
                    const response = yield DataCache.GoogleAuthManager.SendGoogleAuthConfirmRequest(requestData);
                    if (response === null || response === void 0 ? void 0 : response.Success) {
                        const okCallBack = this.get('okCallBack');
                        if (okCallBack != null) {
                            okCallBack(userCodeText);
                        }
                        const closeCallBack = this.get('closeCallBack');
                        if (closeCallBack != null) {
                            closeCallBack();
                        }
                    }
                    else {
                        void this.set('haveError', true);
                    }
                }
            }
            catch (error) {
                console.error('Error encrypting TOTP:', error.message);
            }
        });
    }
    backBtnVisible() {
        const currentStep = this.get('currentStep');
        return currentStep > GoogleAuthenticator.StepInitial;
    }
    nextBtnVisible() {
        const currentStep = this.get('currentStep');
        return currentStep < GoogleAuthenticator.StepsCount;
    }
    finishBtnVisible() {
        const currentStep = this.get('currentStep');
        return currentStep === GoogleAuthenticator.StepsCount;
    }
    copyBtnClick() {
        const secretKey = this.get('secretKeyText');
        if (secretKey != null) {
            void navigator.clipboard.writeText(secretKey);
        }
    }
    handleStepChange(value) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (value === GoogleAuthenticator.StepGoogleAuthRequest) {
                const userID = this.get('userID');
                if (userID != null) {
                    yield this.set('isQRCodeLoaded', false);
                    const response = yield DataCache.GoogleAuthManager.SendGoogleAuthRequest(userID);
                    if ((_a = response.QRCode) === null || _a === void 0 ? void 0 : _a.length) {
                        const processedQRCode = this.processQRCode(response.QRCode, response.FileFormat);
                        void this.set({
                            srcOfQRCode: processedQRCode,
                            secretKeyText: response.Secret2FAKey,
                            isQRCodeLoaded: true
                        });
                    }
                }
            }
            else if (value === GoogleAuthenticator.StepsCount) {
                void this.set({ userCodeText: '', haveError: false });
            }
        });
    }
    /**
     * Processes a QR code array and returns a Base64-encoded data URL.
     *
     * @private
     * @param {number[]} qrCodeArray - The array of numbers representing the QR code.
     * @param {string} QRCodeFileFormat - The file format of the QR code. Possible values: 'svg' or 'png'.
     * @returns {string} The Base64-encoded data URL of the QR code.
     * @throws {Error} If the QRCodeFileFormat is not 'svg' or 'png'.
     */
    processQRCode(qrCodeArray, QRCodeFileFormat) {
        if (QRCodeFileFormat !== 'svg' && QRCodeFileFormat !== 'png') {
            throw new Error("Invalid QRCodeFileFormat. Allowed values are 'svg' or 'png'.");
        }
        const mimeType = QRCodeFileFormat === 'svg' ? 'image/svg+xml' : 'image/png';
        // Correctly handle negative byte values and convert them to unsigned values
        const byteArray = qrCodeArray.map((byte) => (byte < 0 ? byte + 256 : byte));
        // Create a binary string
        const binaryString = byteArray.map((byte) => String.fromCharCode(byte)).join('');
        return `data:${mimeType};base64,${btoa(binaryString)}`;
    }
}
GoogleAuthenticator.StepInitial = 1;
GoogleAuthenticator.StepGoogleAuthRequest = 1;
GoogleAuthenticator.StepsCount = 2;
ContainerControl.extendWith(GoogleAuthenticator, {
    template: GoogleAuthenticatorTemplate,
    data: () => ({
        GoogleAuthenticator,
        backText: 'Back',
        nextText: 'Next',
        finishText: 'Log In',
        okCallBack: null,
        closeCallBack: null,
        userID: null,
        currentStep: GoogleAuthenticator.StepInitial,
        stepProgressLabels: [],
        firstStepText: '',
        secondStepText: '',
        secretKeyText: '',
        srcOfQRCode: null,
        isQRCodeLoaded: false,
        thirdStepText: '',
        userCodeLabel: 'Enter code',
        userCodeText: '',
        haveError: false,
        secretKeyLabel: 'Secret key:',
        errorText: 'Invalid code'
    })
});
